/* @flow */

import type { CookieOptions } from "js-cookie";

import { useState, useCallback, useEffect } from "react";
import { useBrowser } from "@awardit/react-use-browser";
import Cookies from "js-cookie";

const readCookie = (key: string, initial: string, options?: CookieOptions): string => {
  try {
    const serialized = Cookies.get(key);
    return serialized ? serialized : initial;
  }
  catch (e) {
    Cookies.remove(key, options);
    return initial;
  }
};

export const useCookie = (
  key: string,
  initial: string,
  options?: CookieOptions
): { value: string, setValue: (value: string) => void, isRead: boolean} => {
  const browser = useBrowser();
  const [item, setInnerValue] = useState(initial);
  const [cookieRead, setCookieRead] = useState(false);

  useEffect(() => {
    if (browser) {
      setInnerValue(readCookie(key, initial, options));
      setCookieRead(true);
    }
  }, [browser, setInnerValue, initial, key, options]);

  const setValue = useCallback((value: string) => {
    setInnerValue(value);

    Cookies.set(key, value, options);
  }, [key, options, setInnerValue]);

  return { value: item, setValue, isRead: cookieRead };
};

const readCookieGeneric = <T>(key: string, initial: T, options?: CookieOptions): T => {
  try {
    const serialized = Cookies.get(key);
    return serialized ? JSON.parse(serialized) : initial;
  }
  catch (e) {
    Cookies.remove(key, options);
    return initial;
  }
};

export const useCookieGeneric = <T>(
  key: string,
  initial: T,
  options?: CookieOptions
): { value: T, setValue: (value: T) => void, isRead: boolean} => {
  const browser = useBrowser();
  const [item, setInnerValue] = useState(initial);
  const [cookieRead, setCookieRead] = useState(false);

  useEffect(() => {
    if (browser) {
      setInnerValue(readCookieGeneric(key, initial, options));
      setCookieRead(true);
    }
  }, [browser, setInnerValue, initial, key, options]);

  const setValue = useCallback((value: T) => {
    setInnerValue(value);

    Cookies.set(key, JSON.stringify(value), options);
  }, [key, options, setInnerValue]);

  return { value: item, setValue, isRead: cookieRead };
};
